import { z } from 'zod';

import type { AnswerReading } from './answer-document.ts';
import { CUSTOM_STUDY_TYPE_ID, STUDY_TYPE_IDS, type StudyTypeId } from './study-types.ts';

/**
 * The queue a sentence is classified on. The API process stores the request and enqueues it; the
 * worker, which is the only process holding a model, answers it.
 */
export const QUEUE_STUDY_TYPE_SUGGEST = 'study-type.suggest';

/**
 * The longest sentence a researcher may send. Generous for a description of a study and small
 * enough that what the model is asked to read, and what the study row stores, stays bounded.
 */
export const MAX_STUDY_TYPE_SUGGESTION_TEXT_CHARS = 1000;

/**
 * How often the Study Type screen re-reads a pending suggestion — the ask is the study read, the
 * hottest route there is, so well above what a person would notice and well below the few seconds
 * an answer takes — and how long it waits before it stops asking and offers a retry. The worker
 * gives the classification's model call only what is left of that wait, counted from
 * `requestedAt`, so no call runs on after the screen has offered to ask again. Placeholder policy
 * values, the same pair the Materials step polls on.
 */
export const STUDY_TYPE_SUGGESTION_POLL_MS = 1000;
export const STUDY_TYPE_SUGGESTION_WAIT_MS = 30_000;

/**
 * Every Study Type a suggestion may name: the sixteen, and never Custom.
 *
 * Custom is not a closest match to anything — it is what the researcher takes when no match is
 * close, and the screen offers it beside every answer already. A model answering `custom` has
 * therefore answered nothing the screen can use, which is what `unrecognised` says.
 */
export const SUGGESTABLE_STUDY_TYPE_IDS = Object.freeze(
  STUDY_TYPE_IDS.filter(
    (id): id is Exclude<StudyTypeId, typeof CUSTOM_STUDY_TYPE_ID> => id !== CUSTOM_STUDY_TYPE_ID,
  ),
);
export type SuggestableStudyTypeId = (typeof SUGGESTABLE_STUDY_TYPE_IDS)[number];
export const suggestableStudyTypeIdSchema = z.enum(SUGGESTABLE_STUDY_TYPE_IDS);

const SUGGESTABLE = new Set<string>(SUGGESTABLE_STUDY_TYPE_IDS);

/**
 * The rule a classification's answer can break, the reason `parseSuggestedStudyTypeId` refuses it:
 * `study_type`, an answer that is not one of the sixteen ids.
 */
export const STUDY_TYPE_SUGGESTION_ANSWER_RULES = ['study_type'] as const;

export type StudyTypeSuggestionAnswerRule = (typeof STUDY_TYPE_SUGGESTION_ANSWER_RULES)[number];

/**
 * The Study Type a model's answer names, or the `study_type` rule when it names none of the
 * sixteen.
 *
 * Strict on purpose: the answer is compared, after trimming surrounding whitespace, with the
 * catalogue's ids and nothing else. Prose, an id with punctuation around it, a name rather than an
 * id, `custom` and an id the catalogue does not hold are all refused — so no path exists from model
 * output to a stored type the catalogue does not have, and a seventeenth named type cannot be
 * invented by a model that answers confidently. Looked up through a `Set`, which has no inherited
 * keys for a string like `toString` to find.
 */
export const parseSuggestedStudyTypeId = (
  answer: string,
): AnswerReading<SuggestableStudyTypeId, StudyTypeSuggestionAnswerRule> => {
  const candidate = answer.trim();

  return SUGGESTABLE.has(candidate)
    ? { ok: true, value: candidate as SuggestableStudyTypeId }
    : { ok: false, rule: 'study_type' };
};

/**
 * What one request carries through every status: the words the researcher wrote, so the screen can
 * say what it is answering, and the request's own id, so an answer to an earlier sentence can be
 * told apart from an answer to this one.
 */
const suggestionRequestSchema = z.object({
  requestId: z.uuid(),
  text: z.string().min(1).max(MAX_STUDY_TYPE_SUGGESTION_TEXT_CHARS),
  requestedAt: z.iso.datetime(),
});

/**
 * The closest Study Type to a sentence, as a study stores it.
 *
 * - `pending` — the worker has not answered yet.
 * - `ready` — the sentence is closest to `studyTypeId`, which is one of the sixteen.
 * - `unrecognised` — the model answered, and its answer named none of the sixteen.
 * - `unavailable` — no answer could be had: no model is configured, the day's budget is spent, or
 *   the call failed.
 *
 * Only `ready` carries a type, so a stored answer cannot name one without being the answer that
 * has one. One request per study at a time; a new sentence replaces whatever is stored.
 */
export const storedStudyTypeSuggestionSchema = z
  .discriminatedUnion('status', [
    suggestionRequestSchema.extend({ status: z.literal('pending') }),
    suggestionRequestSchema.extend({
      status: z.literal('ready'),
      studyTypeId: suggestableStudyTypeIdSchema,
    }),
    suggestionRequestSchema.extend({ status: z.literal('unrecognised') }),
    suggestionRequestSchema.extend({ status: z.literal('unavailable') }),
  ])
  .readonly();
export type StoredStudyTypeSuggestion = z.infer<typeof storedStudyTypeSuggestionSchema>;
export type StudyTypeSuggestionStatus = StoredStudyTypeSuggestion['status'];

/** How a request is settled: every status but the one it starts in. */
export type StudyTypeSuggestionOutcome =
  | { status: 'ready'; studyTypeId: SuggestableStudyTypeId }
  | { status: 'unrecognised' }
  | { status: 'unavailable' };

/**
 * The job the API process sends. It names the request as well as the study, so a job for a sentence
 * the researcher has since replaced is recognised as stale and spends nothing.
 */
export const studyTypeSuggestPayloadSchema = z.object({
  studyId: z.uuid(),
  requestId: z.uuid(),
});
export type StudyTypeSuggestPayload = z.infer<typeof studyTypeSuggestPayloadSchema>;

/**
 * The stored suggestion, or nothing.
 *
 * A shape this build cannot read is treated as no suggestion rather than as an error, like the
 * poller's observation: the suggestion is a convenience on one screen, and refusing to answer the
 * study read over it would take the whole study down for the sake of a hint.
 */
export const parseStoredStudyTypeSuggestion = (
  stored: unknown,
): StoredStudyTypeSuggestion | null => {
  if (stored === null || stored === undefined) {
    return null;
  }

  const parsed = storedStudyTypeSuggestionSchema.safeParse(stored);

  return parsed.success ? parsed.data : null;
};
